import { maskCPF, maskCNPJ } from './masks';

/**
 * Um único campo de busca: decide `name` ou `document` para a API de listagem de clientes.
 * Documentos: CPF ou CNPJ (numérico ou alfanumérico). O parâmetro `document` é enviado **com pontuação** (máscara).
 *
 * Regra:
 * - Espaço ou acentos → filtro por nome.
 * - Somente A–Z/0–9 (e máscara `.` `/` `-`) **e** com ao menos um dígito → filtro por documento:
 *   - Só dígitos até 11 → máscara de CPF.
 *   - Caso contrário → máscara de CNPJ (numérico ou alfanumérico).
 * - Letras sem dígito (ex.: "Acme") → filtro por nome.
 * - Caso contrário (só símbolos) → sem filtro.
 *
 * @param {string} raw
 * @returns {{ name: string | null, document: string | null }}
 */
export function parseCustomerListSearchQuery(raw) {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
        return { name: null, document: null };
    }

    if (/[\sÀ-ÿ]/.test(trimmed)) {
        return { name: trimmed, document: null };
    }

    const sanitized = trimmed.toUpperCase().replace(/[^A-Z0-9./-]/g, '');
    const alnum = sanitized.replace(/[./-]/g, '');
    const looksLikeDocument =
        alnum.length > 0 &&
        /^[A-Z0-9./-]+$/.test(sanitized) &&
        /\d/.test(alnum);

    if (!looksLikeDocument) {
        if (/[a-zA-ZÀ-ÿ]/.test(trimmed)) {
            return { name: trimmed, document: null };
        }
        return { name: null, document: null };
    }

    const isDigitsOnly = /^\d+$/.test(alnum);
    const hasMaskChars = /[./-]/.test(trimmed);

    if (isDigitsOnly && alnum.length <= 11) {
        return {
            name: null,
            document: hasMaskChars ? trimmed : maskCPF(alnum),
        };
    }

    if (hasMaskChars && isDigitsOnly) {
        return { name: null, document: trimmed };
    }

    return { name: null, document: maskCNPJ(trimmed) };
}
